import {
  popMenu,
  popupTargetFromElement,
} from '@blocksuite/affine-components/context-menu';
import { signal } from '@preact/signals-core';
import { html } from 'lit';

import { animateCalendarSourcePopup } from './calendar-chrome-motion.js';
import { readCalendarSourceEvents } from './calendar-command-session.js';
import {
  CalendarCommandError,
  type CalendarSourceCommandSession,
} from './calendar-command-session.js';
import { displayCalendarId } from './calendar-event-store.js';
import { renderCalendarOverlays } from './grids/calendar-grid-renderer.js';

/** Feed management and overlay selection keep the native renderer and explicit source lifetime. */
export function renderCalendarSourceOverlays(
  session: CalendarSourceCommandSession,
  calendarIds: readonly string[],
  readonly: boolean,
  onError: (error: unknown) => void
) {
  const snapshot = session.getFeedSnapshot();
  const mutate = (action: () => void) => {
    try {
      if (readonly)
        throw new CalendarCommandError('readonly', 'calendar.chrome.readonly');
      action();
    } catch (error) {
      onError(error);
    }
  };
  return renderCalendarOverlays({
    calendarIds,
    subscriptions: snapshot.subscriptions,
    subscriptionStates: snapshot.states,
    visibleCalendarIds: session.view.visibleCalendarIds$.value,
    readonly,
    onSetVisibleCalendarIds: ids =>
      mutate(() => session.view.setVisibleCalendarIds(ids)),
    onRefreshIcsSubscription: subscription =>
      void session.refreshFeed(subscription.id).catch(onError),
    onRemoveIcsSubscription: id => mutate(() => session.removeFeed(id)),
    onSetIcsSubscriptionEnabled: (id, enabled) =>
      mutate(() => session.setFeedEnabled(id, enabled)),
  });
}

export function openCalendarSourceOverlays(
  session: CalendarSourceCommandSession,
  range: { start: Date; end: Date },
  anchor: HTMLElement,
  readonly: boolean,
  onError: (error: unknown) => void
) {
  const revision = signal(0);
  const stop = session.subscribe(() => revision.value++);
  const popup = popMenu(popupTargetFromElement(anchor), {
    options: {
      title: { text: session.view.name$.value },
      onClose: stop,
      items: [
        () => {
          void revision.value;
          const ids = [
            ...new Set(
              [
                ...readCalendarSourceEvents(session.view, range, true),
                ...session.getFeedSnapshot().events,
              ].map(event => displayCalendarId(event.calendarId))
            ),
          ];
          return html`${renderCalendarSourceOverlays(
            session,
            ids,
            readonly,
            onError
          )}`;
        },
      ],
    },
  });
  return animateCalendarSourcePopup(popup);
}
